import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DossierMember } from '../../models/DossierMember';
import { ConfirmModal } from '../../features/confirm-modal/ConfirmModal';
import PersonSelector from '../appointments/PersonSelector';
import Alert from 'react-bootstrap/esm/Alert';
import { Api } from '../../api';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { UserRelationRoleEnum } from '../../api/types';

export interface IRemovePersonFromGroupFormProps {
  groupId: number;
  members: DossierMember[];
  onRemoved: (person: DossierMember) => void;
  onCancel: () => void;
}

export function RemovePersonFromGroupForm({ groupId, onRemoved, onCancel, members }: IRemovePersonFromGroupFormProps): JSX.Element {
  const [removeMember, setRemoveMember] = useState<DossierMember|undefined>(undefined);
  const [removeMemberError, setRemoveMemberError] = useState<string|undefined >(undefined);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const isValid = useMemo<boolean>(() => !removeMemberError, [removeMemberError]);
  const filteredMembers = useMemo<DossierMember[]>(() => {
    const caregivers: DossierMember[] = [];
    const otherMembers: DossierMember[] = [];
    for (const member of members) {
      if (member.role === UserRelationRoleEnum.caregiver) {
        caregivers.push(member);
      } else {
        otherMembers.push(member);
      }
    }

    // Do not allow removing the last caregiver of a group
    if (caregivers.length >= 2) {
      return [...caregivers, ...otherMembers];
    }
    return otherMembers;
  }, [members]);

  const validate = useCallback((): boolean => {
    setRemoveMemberError(undefined);

    let isValid = true;
    if (!removeMember) {
      setRemoveMemberError('Selecteer een persoon');
      isValid = false;
    }

    return isValid;
  }, [removeMember]);

  useEffect(() => {
    if (!isValid) {
      validate();
    }
  }, [removeMember, isValid, validate]);

  const clear = (): void => {
    setRemoveMember(undefined);
    setRemoveMemberError(undefined);
  };

  const handleCancel = (): void => {
    clear();
    onCancel();
  };

  const handleSubmit = (): void => {
    if (!validate() || !removeMember) {
      return;
    }
    setSubmitting(true);

    Api.removeRelationFromChatGroup(groupId, removeMember)
      .then(() => {
        clear();
        onRemoved(removeMember);
      })
      .catch(err => {
        console.error(err);
        NotificationHelper.toastError('Verwijderen uit groep mislukt door een onverwachte fout');
      })
      .finally(() => setSubmitting(false));
  };

  const handleSelectMember = (selectedMember: DossierMember): void => {
    setRemoveMember(selectedMember);
  };

  const renderBody = () => <>
    <PersonSelector persons={filteredMembers} selected={removeMember ? [removeMember] : []} onClickItem={handleSelectMember} />
    { removeMemberError && <Alert className="remove-person-from-group-form__danger" variant="danger">{removeMemberError}</Alert> }
  </>;

  return <ConfirmModal
    title="Persoon verwijderen uit groep"
    body={renderBody()}
    confirmLabel="Verwijder"
    confirmButtonStyle="danger"
    onCancel={handleCancel}
    onConfirm={handleSubmit}
    confirming={submitting}
  />;
}
